import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

const KEY = "applicants.stacks.collapsed";
const load = () => { try { return new Set(JSON.parse(localStorage.getItem(KEY) || "[]")); } catch { return new Set(); } };
const byName = (a, b) => `${a.last_name || ""} ${a.first_name || ""}`.localeCompare(`${b.last_name || ""} ${b.first_name || ""}`, undefined, { sensitivity: "base" });
const ago = (s) => { const d = Math.round((Date.now() - new Date(s)) / 86400000); return d <= 0 ? "today" : d === 1 ? "1 day ago" : `${d} days ago`; };

export function ApplicantStacks({ rows, cols, statuses, isAdmin, onOpen, forceOpen = false }) {
  const [collapsed, setCollapsedState] = useState(load);
  const toggle = (k) => setCollapsedState((prev) => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); try { localStorage.setItem(KEY, JSON.stringify([...n])); } catch {} return n; });
  return (
    <div className="space-y-2" data-testid="applicants-stacks">
      {cols.map(([k, color]) => {
        const list = rows.filter((r) => r.status === k).sort(byName);
        if (!list.length) return null;
        const open = forceOpen || !collapsed.has(k);
        const label = statuses.find((s) => s.key === k)?.label || k;
        return (
          <div key={k} className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={`stack-${k}`}>
            <button type="button" onClick={() => toggle(k)} className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors" aria-expanded={open} data-testid={`stack-toggle-${k}`}>
              {open ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
              <span className={`h-2.5 w-2.5 rounded-full ${color}`} />
              <span className="text-sm font-semibold text-slate-800">{label}</span>
              <span className="text-xs text-slate-500 rounded-full bg-slate-100 px-2 py-0.5" data-testid={`stack-count-${k}`}>{list.length}</span>
              {!open && <span className="ml-2 text-xs text-slate-400 truncate">{list.slice(0, 4).map((a) => `${a.last_name}, ${a.first_name}`).join(" · ")}{list.length > 4 ? ` · +${list.length - 4} more` : ""}</span>}
            </button>
            {open && (
              <div className="divide-y divide-slate-100 border-t border-slate-100">
                {list.map((a) => (
                  <button key={a.id} type="button" onClick={() => onOpen(a)} className="w-full grid grid-cols-[1fr_auto] sm:grid-cols-[minmax(0,2fr)_minmax(0,2fr)_minmax(0,1.5fr)_auto] gap-x-4 items-center px-4 py-2 text-left hover:bg-blue-50/60 transition-colors" data-testid={`applicant-row-${a.id}`}>
                    <span className="text-sm font-medium text-slate-900 truncate"><span className="font-semibold">{a.last_name}</span>, {a.first_name}</span>
                    <span className="hidden sm:block text-xs text-slate-500 truncate">{isAdmin && a.company ? `${a.company} · ` : ""}{a.phone || a.email || ""}</span>
                    <span className="hidden sm:block text-xs text-slate-500 truncate">{a.source === "invite" ? "Invited" : a.source === "tenstreet" ? "Tenstreet" : "Applied"}{a.converted_at ? " · driver file" : ""}</span>
                    <span className="text-[11px] text-slate-400 whitespace-nowrap">{ago(a.updated_at)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
